import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { useEffect, useState } from 'react';

type Theme = 'light' | 'dark' | 'system';

interface ThemeState {
  theme: Theme;
  setTheme: (theme: Theme) => void;
}

const useThemeStore = create<ThemeState>()(
  persist(
    (set) => ({
      theme: 'system',
      setTheme: (theme) => set({ theme }),
    }),
    {
      name: 'feedbackhub-theme',
    }
  )
);

function getSystemTheme(): 'light' | 'dark' {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function applyTheme(theme: Theme) {
  const root = document.documentElement;
  const resolvedTheme = theme === 'system' ? getSystemTheme() : theme;

  root.classList.remove('light', 'dark');
  root.classList.add(resolvedTheme);
}

export function useTheme() {
  const { theme, setTheme } = useThemeStore();
  // Held in state so an OS-level theme flip re-renders consumers. Applying the
  // class to <html> alone kept the DOM right but left the value this hook
  // returns stale, so anything reading it - the sonner Toaster, for one - stayed
  // on the old theme until some unrelated render happened to come along.
  const [systemTheme, setSystemTheme] = useState(getSystemTheme);

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  useEffect(() => {
    if (theme === 'system') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handleChange = () => {
        setSystemTheme(getSystemTheme());
        applyTheme('system');
      };

      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
  }, [theme]);

  const resolvedTheme = theme === 'system' ? systemTheme : theme;

  return {
    theme: resolvedTheme,
    setTheme,
    systemTheme,
    storedTheme: theme,
  };
}
